type UnknownRecord = Record<string, unknown>;

const DEFAULT_USER_FACING_ERROR_MESSAGE =
  "Something went wrong. Please try again.";

const ERROR_CODE_PREFIX =
  /^(INTERNAL_ERROR|VALIDATION_ERROR|NOT_FOUND|CONFLICT|AUTHORIZATION_ERROR):\s*/i;
const STACK_LINE = /^\s*at\s+/;
const CONVEX_DETAILS_SUFFIX = /\s*\|\s*\{[\s\S]*\}\s*$/;
const CONVEX_FUNCTION_TAG = /\[CONVEX\s+[A-Z]\([^)]*\)\]/gi;
const CONVEX_REQUEST_ID = /\[Request ID:[^\]]+\]/gi;

const INTERNAL_ERROR_PATTERNS = [
  /\[CONVEX\b/i,
  /\bConvexError\b/i,
  /\bUncaught\s+(?:Error|ConvexError)\b/i,
  /\bServer Error\b/i,
  /\bCalled by client\b/i,
  /\bReturnsValidationError\b/i,
  /\bvalue does not match validator\b/i,
  /\bValidator:\s*v\./i,
  /\bPath:\s*\./i,
  /\bv\.(?:id|object|union|string|number|boolean|array|literal|optional)\(/i,
  /\b(actions|queries|mutations)\/[A-Za-z0-9_/-]+/i,
];

const NETWORK_ERROR_PATTERNS = [
  /\bconnection lost\b/i,
  /\baction was in flight\b/i,
  /\brequest was in flight\b/i,
  /\bjson parse error:\s*unexpected end of input\b/i,
  /\bunexpected end of json input\b/i,
  /\bfetch failed\b/i,
  /\bfailed to fetch\b/i,
  /\bnetwork request failed\b/i,
  /\bnetwork error\b/i,
  /\bnetworkerror\b/i,
  /\boffline\b/i,
];

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function getKnownDuplicateMessage(message: string): string | null {
  const normalized = message.toLowerCase();

  if (normalized.includes("user with this email already exists")) {
    return "That email is already linked to an account. Sign in or use another email.";
  }
  if (normalized.includes("driver with this email already exists")) {
    return "That email is already linked to a driver profile. Sign in or use another email.";
  }
  if (normalized.includes("username is already taken")) {
    return "That username is already taken. Try another one.";
  }
  if (normalized.includes("business with this name already exists")) {
    return "That business name is already in use. Choose a different name.";
  }
  if (normalized.includes("already has a business profile")) {
    return "This account already has a business profile.";
  }
  if (normalized.includes("business name is required")) {
    return "Enter a business name.";
  }

  return null;
}

function extractCandidateMessage(error: unknown): string | undefined {
  if (typeof error === "string") return error;
  if (error instanceof Error && typeof error.message === "string") {
    return error.message;
  }
  if (!isRecord(error)) return undefined;

  const directMessage = error.message;
  if (typeof directMessage === "string" && directMessage.trim().length > 0) {
    return directMessage;
  }

  const nestedCandidates = [
    error.error,
    error.data,
    error.cause,
    isRecord(error.data) ? error.data.error : undefined,
  ];

  for (const candidate of nestedCandidates) {
    const extracted = extractCandidateMessage(candidate);
    if (typeof extracted === "string" && extracted.trim().length > 0) {
      return extracted;
    }
  }

  return undefined;
}

function cleanRawErrorMessage(message: string): string {
  const withoutJsonDetails = message.trim().replace(CONVEX_DETAILS_SUFFIX, "");
  const lines = withoutJsonDetails
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .filter((line) => !STACK_LINE.test(line));

  return (lines[0] || withoutJsonDetails)
    .replace(CONVEX_FUNCTION_TAG, "")
    .replace(CONVEX_REQUEST_ID, "")
    .replace(/^.*?Uncaught Error:\s*/i, "")
    .replace(/^.*?Uncaught ConvexError:\s*/i, "")
    .replace(/^.*?ConvexError:\s*/i, "")
    .replace(/^Error:\s*/i, "")
    .replace(/^Server Error\s*/i, "")
    .replace(/\s*Called by client[\s\S]*$/i, "")
    .replace(ERROR_CODE_PREFIX, "")
    .trim();
}

export function hasInternalErrorDetails(error: unknown): boolean {
  const message = extractCandidateMessage(error);
  if (!message) return false;
  return INTERNAL_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

export function sanitizeUserFacingErrorMessage(
  error: unknown,
  fallback = DEFAULT_USER_FACING_ERROR_MESSAGE,
): string {
  const rawMessage = extractCandidateMessage(error);
  if (!rawMessage) return fallback;

  if (NETWORK_ERROR_PATTERNS.some((pattern) => pattern.test(rawMessage))) {
    return "Network unstable. Please try again.";
  }

  if (
    /\btoo many requests\b/i.test(rawMessage) ||
    /\brate[_ -]?limit(?:ed)?\b/i.test(rawMessage)
  ) {
    return "You're doing that a little too quickly. Please wait a moment and try again.";
  }

  if (
    /\bnot authenticated\b/i.test(rawMessage) ||
    /\bsession expired\b/i.test(rawMessage) ||
    /\bauthentication required\b/i.test(rawMessage) ||
    /\binvalid or expired token\b/i.test(rawMessage)
  ) {
    return "Please sign in and try again.";
  }

  if (
    /\baccess denied\b/i.test(rawMessage) ||
    /\bforbidden\b/i.test(rawMessage) ||
    /\bnot authorized\b/i.test(rawMessage) ||
    /\binsufficient permissions\b/i.test(rawMessage) ||
    /\bAUTHORIZATION_ERROR\b/i.test(rawMessage)
  ) {
    return "You do not have permission to do that.";
  }

  const cleanedMessage = cleanRawErrorMessage(rawMessage);
  const knownDuplicateMessage = getKnownDuplicateMessage(cleanedMessage);
  if (knownDuplicateMessage) {
    return knownDuplicateMessage;
  }

  if (
    cleanedMessage.length === 0 ||
    cleanedMessage.toLowerCase() === "an unexpected error occurred" ||
    INTERNAL_ERROR_PATTERNS.some(
      (pattern) => pattern.test(rawMessage) || pattern.test(cleanedMessage),
    )
  ) {
    return fallback;
  }

  return cleanedMessage;
}
